import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { LocalizedMessage, standardDateNoSecondsOptions } from 'intl/LocalizedComponents';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _max from 'lodash/max';
import _reject from 'lodash/reject';
import _size from 'lodash/size';
import _without from 'lodash/without';
import PropTypes from 'prop-types';
// Permissions
import OnlyWith from 'components/permissions/OnlyWith';
import permissions from 'constants/Permissions';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withPermissions from 'hocs/WithPermissions';
import withStateReinit from 'hocs/WithStateReinit';
import withConfirmDialog from 'hocs/WithConfirmDialog';
// Material UI
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
// Core components
import Avatar from 'components/core/Avatar';
import { TextButton, ContainedButton } from 'components/core/Button';
import Card from 'components/core/Card';
import Checkbox from 'components/core/Checkbox';
import DateTimePicker2 from 'components/core/DateTimePicker2/DateTimePicker2';
import Divider from 'components/core/Divider';
import List from 'components/core/List';
import TextField from 'components/core/TextField';
import Typography from 'components/core/Typography';
// Models
import { sanitizeDesc } from 'models/accessWay';
import mCarpark from 'models/cpm/carpark';
import mCustomer from 'models/cpm/customer';
import mParkingCategory from 'models/cpm/parkingCategory';
import mParking from 'models/cpm/parking';
import mPlanning from 'models/cpm/planning';
// Custom
import {
  cpmCustomerDataShape,
  cpmPlanningDataShape,
  cpmDoorDataShape,
  cpmParkingCategoriesShape,
  errorShape,
} from 'constants/PropTypesShapes';
import { cpmGetCarparkCustomer, cpmGetCarparkUniqueKeypadCode } from 'actions';
import { invoke, sanitizeInt } from 'utils/Tools';
import { dateGenerator, dateFormatter } from 'utils/DateUtils';
import {
  buildFormErrors,
  cpmParkingAddConstraints,
  keypadCodeConstraints,
  licensePlateConstraints,
} from 'utils/Forms';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import AppIcon from 'components/icons/AppIcon';
import { AddContainedButton } from 'components/buttons/ContainedButtons';
import { DeleteIconButton } from 'components/buttons/IconButtons';
import NumberInputField from 'components/inputFields/NumberInputField';
import ResourcesSelectFieldWithCreateButton from 'components/inputFields/ResourcesSelectFieldWithCreateButton';
import InformationItem from 'components/items/InformationItem';
import Callout from 'components/misc/Callout';
import CustomerChooseList from 'cpmCarparks/assets/customers/CustomerChooseList';
import CustomerDesc from 'cpmCarparks/assets/customers/CustomerDesc';
import CarparkDoorItem from 'cpmCarparks/assets/main/CarparkDoorItem';
import PlanningInfoButton from 'cpmCarparks/assets/plannings/PlanningInfoButton';
import { KEYPAD_CODE_MAX_LENGTH } from 'constants/App';
// Utils
import RegexUtils from 'utils/RegexUtils';

const StepIcon = ({ active, completed, icon }) => {
  const icons = {
    1: 'itemCustomer',
    2: 'itemInformation',
    3: 'itemAccessWay2',
    4: 'itemConfig',
  };

  return (
    <Avatar primary={active || completed} size={30} style={{ marginLeft: -3 }}>
      <AppIcon id={icons[icon]} />
    </Avatar>
  );
};

StepIcon.propTypes = {
  active: PropTypes.bool.isRequired,
  completed: PropTypes.bool.isRequired,
  icon: PropTypes.number.isRequired,
};

class ParkingAddForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleNext = this.handleNext.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.handleStep = this.handleStep.bind(this);
    this.handleKeypadCodeChange = this.handleKeypadCodeChange.bind(this);
    this.handleAddKeypadCode = this.handleAddKeypadCode.bind(this);
    this.handleDeleteKeypadCode = this.handleDeleteKeypadCode.bind(this);
    this.handleLicensePlateChange = this.handleLicensePlateChange.bind(this);
    this.handleAddVehicle = this.handleAddVehicle.bind(this);
    this.handleDeleteVehicle = this.handleDeleteVehicle.bind(this);
    this.handleToggleDoor = this.handleToggleDoor.bind(this);
    this.handleValidate = this.handleValidate.bind(this);

    // Used to store the id of an added planning
    this.addedPlanningId = null;

    const { fixedCustomer } = this.props;

    this.state = {
      stepIndex: fixedCustomer ? 1 : 0,
      completed: fixedCustomer ? 1 : 0,
      keypadCode: '',
      licensePlate: '',
    };
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const {
      onRef,
      fixedCustomer,
      doors,
      onFieldsChange,
    } = this.props;
    onRef(this);
    // Set customer if needed
    if (fixedCustomer) {
      onFieldsChange({ customer: fixedCustomer });
    }

    // By default, all doors are enabled
    onFieldsChange({
      doorsIds: _map(doors, 'id'),
    });
  }

  componentDidUpdate(prevProps) {
    const {
      id,
      customerAdd,
      addedCustomer,
      planningAdd,
      planningsLoading,
      uniqueKeypadCode,
      errors,
      onFieldsChange,
      handleGetCustomer,
      parkingCategories,
      fields: {
        customer,
      },
    } = this.props;
    const {
      customerAdd: prevCustomerAdd, addedCustomer: prevAddedCustomer,
      planningAdd: prevPlanningAdd, planningsLoading: prevPlanningsLoading,
      uniqueKeypadCode: prevUniqueKeypadCode,
      errors: prevErrors,
      fields: {
        customer: prevCustomer,
      },
    } = prevProps;

    if (mCustomer.getParkingCategoryId(customer) !== mCustomer.getParkingCategoryId(prevCustomer)) {
      const customerParkingCategory = _find(parkingCategories, ['id', mCustomer.getParkingCategoryId(customer)]);
      const isAssignedOnly = mParkingCategory.isAssignedOnly(customerParkingCategory);
      if (isAssignedOnly) {
        onFieldsChange({ isAssigned: true });
      }
    }
    // Catch if a new customer was added to load it
    if (customerAdd.added && !prevCustomerAdd.added) {
      const { id: customerId } = customerAdd.result;
      handleGetCustomer({
        carparkId: id,
        id: customerId,
      });
    }
    // Catch end of add customer to select it and switch to next step
    if (addedCustomer && !prevAddedCustomer) {
      // Set customer
      onFieldsChange({
        customer: addedCustomer,
      });

      // Switch to next step
      invoke(() => {
        this.setState({
          stepIndex: 1,
        });
      });
    }

    // Catch if a new planning was added
    if (planningAdd.added && !prevPlanningAdd.added) {
      this.addedPlanningId = planningAdd.result.id;
    }
    // Catch end of plannings reload to set the added planning
    if (!planningsLoading && prevPlanningsLoading) {
      onFieldsChange({
        planningId: this.addedPlanningId,
      });
    }

    // Catch if a unique code has been generated
    if (uniqueKeypadCode.result && !prevUniqueKeypadCode.result) {
      const {
        fields: { keypadCodes },
      } = this.props;
      onFieldsChange({
        keypadCodes: [...keypadCodes, uniqueKeypadCode.result],
      });
    }

    // Catch errors
    if (errors && !prevErrors) {
      let stepIndex;
      if (_has(errors, 'customer')) {
        stepIndex = 0;
      } else if (
        _has(errors, 'begin')
        || _has(errors, 'end')
        || _has(errors, 'price')
      ) {
        stepIndex = 1;
      }
      if (stepIndex) {
        // Switch to next step
        invoke(() => {
          this.setState({
            stepIndex,
          });
        });
      }
    }
  }

  // Handlers
  handleNext() {
    this.setState((state) => ({
      stepIndex: state.stepIndex + 1,
      completed: _max([state.completed, state.stepIndex + 1]),
    }));
  }

  handlePrev() {
    this.setState((state) => ({
      stepIndex: state.stepIndex - 1,
    }));
  }

  handleStep(stepIndex) {
    this.setState({
      stepIndex,
    });
  }

  handleKeypadCodeChange(value) {
    this.setState({
      keypadCode: sanitizeDesc(value, 'keypad_code'),
    });
  }

  handleAddKeypadCode() {
    const { keypadCode } = this.state;
    const {
      fields: { keypadCodes },
      onErrorsChange,
      onFieldsChange,
    } = this.props;
    if (!_isEmpty(keypadCode) && !_includes(keypadCodes, keypadCode)) {
      // Check errors
      const errors = buildFormErrors({ keypadCode }, keypadCodeConstraints);
      onErrorsChange(errors);
      // Add keypad if no error
      if (!errors) {
        onFieldsChange({
          keypadCodes: [...keypadCodes, keypadCode],
        });
        this.setState({
          keypadCode: '',
        });
      }
    }
  }

  handleDeleteKeypadCode(keypadCode) {
    const {
      fields: { keypadCodes },
      onFieldsChange,
    } = this.props;
    // Add license plate to array of vehicles
    onFieldsChange({
      keypadCodes: _without(keypadCodes, keypadCode),
    });
  }

  handleLicensePlateChange(value) {
    this.setState({
      licensePlate: sanitizeDesc(value, 'license_plate'),
    });
  }

  handleAddVehicle() {
    const { licensePlate } = this.state;
    const {
      fields: { vehicles },
      onErrorsChange,
      onFieldsChange,
    } = this.props;
    if (
      !_isEmpty(licensePlate)
      && !_find(vehicles, ['licensePlate', licensePlate])
    ) {
      // Check errors
      const errors = buildFormErrors({ licensePlate }, licensePlateConstraints);
      onErrorsChange(errors);
      // Add vehicle if no error
      if (!errors) {
        onFieldsChange({
          vehicles: [...vehicles, { licensePlate }],
        });
        this.setState({
          licensePlate: '',
        });
      }
    }
  }

  handleDeleteVehicle(vehicle) {
    const {
      fields: { vehicles },
      onFieldsChange,
    } = this.props;
    // Add license plate to array of vehicles
    onFieldsChange({
      vehicles: _reject(vehicles, ['licensePlate', vehicle.licensePlate]),
    });
  }

  handleToggleDoor(doorId) {
    const {
      fields: { doorsIds },
      onFieldsChange,
    } = this.props;
    // Add or remove door from list
    onFieldsChange({
      doorsIds: _includes(doorsIds, doorId)
        ? _without(doorsIds, doorId)
        : [...doorsIds, doorId],
    });
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: {
        automaticKeypadCodesQuantity,
        customer,
        spacesQuantity,
        begin,
        end,
        comments,
        keypadCodes,
        vehicles,
        insideQuantity,
        isAssigned,
        outsideQuantity,
        outsideQuantityPermanentExit,
        planningId,
        doorsIds,
        restrictedByForbiddenEntry,
        restrictedByPermanentExit,
        restrictedByForbiddenExit,
        restrictedByAntipassbackEntrance,
        restrictedByAntipassbackExit,
      },
      handleDisplayConfirm,
      timezone,
      forbidManualKeypadCodes,
      hasPermission,
      onErrorsChange,
      onValidate,
      parkingCategories,
    } = this.props;

    const errors = buildFormErrors(
      {
        customer,
        spacesQuantity,
        insideQuantity,
      },
      cpmParkingAddConstraints,
    );
    // Apply errors
    onErrorsChange(errors);

    // Transform boolean value to parking type
    const type = isAssigned
      ? mParking.getTypeAssigned
      : mParking.getTypeRegular;

    // No error => validate form
    if (!errors) {
      const beginInUTC = dateFormatter.toServerFormat(begin, timezone, { seconds: 0 });
      const endInUTC = dateFormatter.toServerFormat(end, timezone, { seconds: 0 });

      // isAssigned checked warning
      const customerParkingCategory = _find(parkingCategories, ['id', mCustomer.getParkingCategoryId(customer)]);
      const isAssignedOnly = mParkingCategory.isAssignedOnly(customerParkingCategory);

      const createParking = () => onValidate(mParking.serializeAdd({
        automaticKeypadCodesQuantity: forbidManualKeypadCodes
          ? automaticKeypadCodesQuantity
          : null,
        begin: beginInUTC,
        end: endInUTC,
        comments,
        customerId: customer.id,
        keypadCodes: forbidManualKeypadCodes ? null : keypadCodes,
        vehicles,
        insideQuantity,
        outsideQuantity,
        outsideQuantityPermanentExit,
        doors: [...doorsIds],
        planningId,
        restrictedByForbiddenEntry,
        restrictedByPermanentExit,
        restrictedByForbiddenExit,
        restrictedByAntipassbackEntrance,
        restrictedByAntipassbackExit,
        spacesQuantity,
        type,
      }, hasPermission));

      if (isAssignedOnly && !isAssigned) {
        handleDisplayConfirm(
          { id: 'confirm.addWithoutAssignedForAssignedOnly' },
          createParking,
          {
            confirmLabel: 'confirm',
          },
        );
      } else {
        createParking();
      }

      return true;
    }

    // Errors: go to the appropriate step
    let stepIndex;
    if (_has(errors, 'details.customer')) {
      stepIndex = 0;
    } else if (
      _has(errors, 'details.end')
      || _has(errors, 'details.spacesQuantity')
    ) {
      stepIndex = 1;
    } else {
      stepIndex = 2;
    }
    this.setState({ stepIndex });

    return false;
  }

  render() {
    const {
      stepIndex, completed, keypadCode, licensePlate,
    } = this.state;
    const {
      id, timezone, fixedCustomer,
      plannings, planningsLoading, doors,
      uniqueKeypadCode, handleGetUniqueKeypadCode,
      forbidManualKeypadCodes,
      parkingCategories,
      fields: {
        automaticKeypadCodesQuantity,
        customer, spacesQuantity, begin, end, comments,
        keypadCodes, vehicles, insideQuantity, outsideQuantity,
        planningId, doorsIds, restrictedByForbiddenEntry, restrictedByPermanentExit, restrictedByForbiddenExit,
        restrictedByAntipassbackEntrance, restrictedByAntipassbackExit, isAssigned,
      }, errors,
      onFieldsChange, onAddCustomer, onAddPlanning,
    } = this.props;

    // isAssigned checked warning
    const customerParkingCategory = _find(parkingCategories, ['id', mCustomer.getParkingCategoryId(customer)]);
    const isAssignedOnly = mParkingCategory.isAssignedOnly(customerParkingCategory);
    const displayWarningAssigned = isAssignedOnly && !isAssigned;

    // Dates
    const beginDate = dateGenerator.dateFromTimezone(begin, timezone);
    const endDate = dateGenerator.dateFromTimezone(end, timezone);
    const nowDate = dateGenerator.dateFromTimezone(dateGenerator.now(), timezone);

    // Permissions
    const { pCpm: { planningRead, planningWrite } } = permissions;

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          <div className="col-12">
            <Stepper
              activeStep={stepIndex}
              orientation="vertical"
              style={{ backgroundColor: 'rgba(0,0,0,0)' }}
            >
              <Step>
                <StepLabel StepIconComponent={StepIcon}>
                  {customer ? (
                    <CustomerDesc customer={customer} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-between">
                      <LocalizedMessage id="title.chooseCarparkUser" />
                      <AddContainedButton
                        size="small"
                        onClick={onAddCustomer}
                      />
                    </div>
                  )}
                </StepLabel>
                <StepContent>
                  <div className="row">
                    {_has(errors, 'customer') && (
                      <div className="col-12 pb-2">
                        <Callout severity="error" withInfoIcon>
                          {errors.customer}
                        </Callout>
                      </div>
                    )}
                    <div className="col-12">
                      <CustomerChooseList
                        id={id}
                        timezone={timezone}
                        withOnDemandCategory={false}
                        fullWidth={false}
                        withChoose
                        withParking
                        permanentFiltersSpecs={[
                          { key: 'onDemand', value: false },
                        ]}
                        selectedCustomer={customer}
                        onSelectCustomer={(value) => onFieldsChange({ customer: value }, () => {
                          this.handleNext();
                        })}
                      />
                    </div>
                    <div className="col-12 pt-3">
                      <ContainedButton
                        disabled={!customer}
                        label={<LocalizedMessage id="button.next" />}
                        onClick={this.handleNext}
                      />
                    </div>
                  </div>
                </StepContent>
              </Step>
              <Step>
                <StepButton
                  completed={completed > 0}
                  onClick={() => this.handleStep(1)}
                >
                  <StepLabel StepIconComponent={StepIcon}>
                    <LocalizedMessage id="title.generalInfo" />
                  </StepLabel>
                </StepButton>
                <StepContent>
                  <div className="d-flex">
                    <FormHelper
                      elements={[
                        {
                          title: <LocalizedMessage id="field.spacesQuantity" />,
                          content: (
                            <LocalizedMessage id="help.parking.formSpacesQuantity" />
                          ),
                        },
                        {
                          title: <LocalizedMessage id="field.period" />,
                          content: (
                            <LocalizedMessage id="help.parking.formPeriod" />
                          ),
                        },
                      ]}
                    />
                    <div style={{ flex: 1 }}>
                      <Card>
                        <div className="row">
                          <div className="col-12">
                            <NumberInputField
                              label={(
                                <LocalizedMessage
                                  id="field.spacesQuantityWithMax"
                                  values={{ max: '128' }}
                                />
                              )}
                              errorText={_get(errors, 'spacesQuantity')}
                              min={1}
                              max={128}
                              value={spacesQuantity}
                              required
                              sanitizer={sanitizeInt}
                              inputMaxWidth={70}
                              onChange={(value) => onFieldsChange({ spacesQuantity: value, outsideQuantity: value - insideQuantity })}
                            />
                          </div>
                          <div className="col-12 col-lg-9 my-3">
                            <DateTimePicker2
                              name="begin"
                              calendarType="begin"
                              clearable
                              hideIcons
                              minDate={nowDate}
                              maxDate={endDate}
                              errorText={_get(errors, 'begin')}
                              date={beginDate}
                              beginDate={beginDate}
                              endDate={endDate}
                              labelDateId="label.start"
                              placeholderId="placeholder.start"
                              calendarLabelId="calendarLabel.start"
                              onChange={(newBegin) => onFieldsChange({ begin: newBegin })}
                            />
                          </div>
                          <div className="col-12 col-lg-9">
                            <DateTimePicker2
                              name="end"
                              calendarType="end"
                              clearable
                              hideIcons
                              minDate={beginDate || nowDate}
                              errorText={_get(errors, 'end')}
                              date={endDate}
                              beginDate={beginDate}
                              endDate={endDate}
                              labelDateId="label.end"
                              placeholderId="placeholder.end"
                              calendarLabelId="calendarLabel.end"
                              onChange={(newEnd) => onFieldsChange({ end: newEnd })}
                            />
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                  <div className="d-flex pt-3">
                    <FormHelper
                      elements={[
                        {
                          title: <LocalizedMessage id="field.assignedSpace" />,
                          content: (
                            <LocalizedMessage id="help.parking.formAssignSpace" />
                          ),
                        },
                      ]}
                    />
                    <div style={{ flex: 1 }}>
                      <Card>
                        <div className="row">
                          <div className="col-12">
                            <Typography type="subtitle2">
                              <LocalizedMessage id="field.assignedSpace" />
                            </Typography>
                          </div>
                          <div className="col-12">
                            {displayWarningAssigned
                              ? (
                                <Callout className="py-2" severity="warning" withInfoIcon>
                                  <LocalizedMessage id="info.addWithoutAssignedForAssignedOnly" />
                                </Callout>
                              ) : null}
                            <Checkbox
                              label={
                                <LocalizedMessage id="field.assignedSpace" />
                              }
                              checked={isAssigned}
                              onChange={(checked) => onFieldsChange({ isAssigned: checked })}
                            />
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                  <div className="d-flex pt-3">
                    <FormHelper elements={[]} />
                    <div style={{ flex: 1 }}>
                      <Card>
                        <div className="row">
                          <div className="col-12">
                            <TextField
                              label={<LocalizedMessage id="field.comments" />}
                              value={comments}
                              errorText={_get(errors, 'comments')}
                              onChange={(value) => onFieldsChange({ comments: value })}
                            />
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                  <div className="pt-4 left-spaced-buttons d-flex">
                    {!fixedCustomer && (
                      <TextButton
                        label={<LocalizedMessage id="button.prev" />}
                        onClick={this.handlePrev}
                      />
                    )}
                    <ContainedButton
                      label={<LocalizedMessage id="button.next" />}
                      onClick={this.handleNext}
                    />
                  </div>
                </StepContent>
              </Step>
              <Step>
                <StepButton
                  completed={completed > 1}
                  onClick={() => this.handleStep(2)}
                >
                  <StepLabel StepIconComponent={StepIcon}>
                    <LocalizedMessage id="title.accessWaysCarpark" />
                  </StepLabel>
                </StepButton>
                <StepContent>
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex">
                        <FormHelper
                          elements={forbidManualKeypadCodes ? [
                            {
                              title: (<LocalizedMessage id="field.accessCodes" />),
                              content: <LocalizedMessage id="help.parking.formAccessCodesForbidKeypadCodes" />,
                            },
                          ] : [
                            {
                              title: (<LocalizedMessage id="field.accessCodes" />),
                              content: <LocalizedMessage id="help.parking.formAccessCodes" />,
                            },
                            {
                              content: (<LocalizedMessage id="help.parking.formGenerateUniqueCode" />),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.accessCodes" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              {forbidManualKeypadCodes ? (
                                <div className="col-12">
                                  <NumberInputField
                                    label={
                                      <LocalizedMessage id="field.keypadCodesQuantity" />
                                    }
                                    min={0}
                                    value={automaticKeypadCodesQuantity}
                                    required
                                    sanitizer={sanitizeInt}
                                    inputMaxWidth={70}
                                    onChange={(value) => onFieldsChange({
                                      automaticKeypadCodesQuantity: value,
                                    })}
                                  />
                                </div>
                              ) : (
                                // Section add manual access code
                                <div className="col-12">
                                  <Card
                                    padding="dense"
                                    outlined
                                    title={(
                                      <Typography type="subtitle">
                                        <LocalizedMessage id="title.addAccessCode" />
                                      </Typography>
                                    )}
                                  >
                                    <div className="d-flex align-items-end flex-wrap justify-content-between">
                                      <div
                                        style={{ width: 200 }}
                                        className="pl-2 pb-1"
                                      >
                                        <TextField
                                          margin="none"
                                          label={
                                            <LocalizedMessage id="field.keypadCode" />
                                          }
                                          value={keypadCode}
                                          inputMaxWidth={150}
                                          maxLength={KEYPAD_CODE_MAX_LENGTH}
                                          errorText={_get(errors, 'keypadCode')}
                                          onChange={this.handleKeypadCodeChange}
                                          onEnterPress={
                                            this.handleAddKeypadCode
                                          }
                                        />
                                      </div>
                                      <ContainedButton
                                        label={
                                          <LocalizedMessage id="button.validate" />
                                        }
                                        size="small"
                                        onClick={this.handleAddKeypadCode}
                                      />
                                    </div>
                                    <div className="d-flex justify-content-center pt-3">
                                      <TextButton
                                        size="small"
                                        loading={uniqueKeypadCode.loading}
                                        label={
                                          <LocalizedMessage id="button.generateUniqueCode" />
                                        }
                                        onClick={() => handleGetUniqueKeypadCode({
                                          carparkId: id,
                                        })}
                                      />
                                    </div>
                                  </Card>
                                  <div className="pt-3">
                                    <Card
                                      padding="dense"
                                      outlined
                                      defaultBackground
                                      title={(
                                        <Typography type="subtitle">
                                          <LocalizedMessage id="title.accessCodesDefined" />
                                        </Typography>
                                      )}
                                    >
                                      <List disablePadding>
                                        {_map(keypadCodes, (k, index) => (
                                          <React.Fragment key={index}>
                                            <InformationItem
                                              light
                                              icon="cAwTypeKCode"
                                              value={k}
                                              rightIcon={(
                                                <DeleteIconButton
                                                  size="small"
                                                  onClick={() => this.handleDeleteKeypadCode(
                                                    k,
                                                  )}
                                                />
                                              )}
                                            />
                                            {index < _size(keypadCodes) - 1 && (
                                              <Divider variant="middle" />
                                            )}
                                          </React.Fragment>
                                        ))}
                                        {_isEmpty(keypadCodes) && (
                                          <div className="px-3">
                                            <em>
                                              <LocalizedMessage id="info.useFormAddAccessCode" />
                                            </em>
                                          </div>
                                        )}
                                      </List>
                                    </Card>
                                  </div>
                                </div>
                              )}
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 pt-3">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="field.authorizedVehicles" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formVehicles" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.authorizedVehicles" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              <div className="col-12">
                                <Card
                                  padding="dense"
                                  outlined
                                  title={(
                                    <Typography type="subtitle">
                                      <LocalizedMessage id="title.addVehicle" />
                                    </Typography>
                                  )}
                                >
                                  <div className="d-flex align-items-end flex-wrap justify-content-between">
                                    <div
                                      style={{ width: 200 }}
                                      className="pl-2 pb-1"
                                    >
                                      <TextField
                                        margin="none"
                                        label={
                                          <LocalizedMessage id="field.licensePlate" />
                                        }
                                        value={licensePlate}
                                        inputMaxWidth={150}
                                        errorText={_get(errors, 'licensePlate')}
                                        onChange={this.handleLicensePlateChange}
                                        onEnterPress={this.handleAddVehicle}
                                      />
                                    </div>
                                    <ContainedButton
                                      label={
                                        <LocalizedMessage id="button.validate" />
                                      }
                                      size="small"
                                      onClick={this.handleAddVehicle}
                                    />
                                  </div>
                                </Card>
                                <div className="pt-3">
                                  <Card
                                    padding="dense"
                                    outlined
                                    defaultBackground
                                    title={(
                                      <Typography type="subtitle">
                                        <LocalizedMessage id="title.vehiclesDefined" />
                                      </Typography>
                                    )}
                                  >
                                    <List disablePadding>
                                      {_map(vehicles, (vehicle, index) => (
                                        <React.Fragment key={index}>
                                          <InformationItem
                                            light
                                            icon="cAwTypeLPlate"
                                            value={vehicle.licensePlate}
                                            rightIcon={(
                                              <DeleteIconButton
                                                size="small"
                                                onClick={() => this.handleDeleteVehicle(
                                                  vehicle,
                                                )}
                                              />
                                            )}
                                          />
                                          {index < _size(vehicles) - 1 && (
                                            <Divider variant="middle" />
                                          )}
                                        </React.Fragment>
                                      ))}
                                      {_isEmpty(vehicles) && (
                                        <div className="px-3">
                                          <em>
                                            <LocalizedMessage id="info.useFormAddVehicle" />
                                          </em>
                                        </div>
                                      )}
                                    </List>
                                  </Card>
                                </div>
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 left-spaced-buttons d-flex">
                    <TextButton
                      label={<LocalizedMessage id="button.prev" />}
                      onClick={this.handlePrev}
                    />
                    <ContainedButton
                      label={<LocalizedMessage id="button.next" />}
                      onClick={this.handleNext}
                    />
                  </div>
                </StepContent>
              </Step>
              <Step>
                <StepButton
                  completed={completed > 2}
                  onClick={() => this.handleStep(3)}
                >
                  <StepLabel StepIconComponent={StepIcon}>
                    <LocalizedMessage id="title.configuration" />
                  </StepLabel>
                </StepButton>
                <StepContent>
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="title.initialState" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formInitialState" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="title.initialState" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              <div className="col-12 col-lg-6">
                                <NumberInputField
                                  label={
                                    <LocalizedMessage id="field.insideVehicles" />
                                  }
                                  errorText={_get(errors, 'insideQuantity')}
                                  min={0}
                                  value={insideQuantity}
                                  required
                                  sanitizer={sanitizeInt}
                                  inputMaxWidth={70}
                                  onChange={(value) => onFieldsChange({ insideQuantity: value })}
                                />
                              </div>
                              <div className="col-12 col-lg-6">
                                <NumberInputField
                                  label={
                                    <LocalizedMessage id="field.outsideVehicles" />
                                  }
                                  errorText={_get(errors, 'outsideQuantity')}
                                  min={0}
                                  value={outsideQuantity}
                                  sanitizer={sanitizeInt}
                                  inputMaxWidth={70}
                                  onChange={(value) => onFieldsChange({ outsideQuantity: value })}
                                />
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 pt-3">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="field.antipassback" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formAntipassback" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.antipassback" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              <div className="col-12">
                                <Checkbox
                                  label={
                                    <LocalizedMessage id="field.restrictedEntrance" />
                                  }
                                  checked={restrictedByAntipassbackEntrance}
                                  onChange={(checked) => onFieldsChange({
                                    restrictedByAntipassbackEntrance: checked,
                                  })}
                                />
                              </div>
                              <div className="col-12">
                                <Checkbox
                                  label={
                                    <LocalizedMessage id="field.restrictedExit" />
                                  }
                                  checked={restrictedByAntipassbackExit}
                                  onChange={(checked) => onFieldsChange({
                                    restrictedByAntipassbackExit: checked,
                                  })}
                                />
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                    <OnlyWith
                      permissions={{
                        needed: planningRead,
                        permissionsOrContext: 'carparkPermissions',
                      }}
                    >
                      <div className="col-12 pt-3">
                        <div className="d-flex">
                          <FormHelper
                            elements={[
                              {
                                title: <LocalizedMessage id="field.planning" />,
                                content: (
                                  <LocalizedMessage id="help.parking.formPlanning" />
                                ),
                              },
                            ]}
                          />
                          <div style={{ flex: 1 }}>
                            <Card
                              title={(
                                <Typography type="subtitle2">
                                  <LocalizedMessage id="field.planningApplied" />
                                </Typography>
                              )}
                            >
                              <ResourcesSelectFieldWithCreateButton
                                loading={planningsLoading}
                                infoButton={
                                  !planningsLoading && planningId ? (
                                    <div className="ml-1">
                                      <PlanningInfoButton
                                        planningId={planningId}
                                      />
                                    </div>
                                  ) : null
                                }
                                selectFieldProps={{
                                  labelId: 'field.planning',
                                  value: planningId,
                                  resources: mPlanning.sortElements(plannings),
                                  withNullItem: true,
                                  displayKey: 'name',
                                  onChange: (value) => onFieldsChange({ planningId: value }),
                                }}
                                addPermissions={{
                                  needed: planningWrite,
                                  permissionsOrContext: 'carparkPermissions',
                                }}
                                onAddResource={onAddPlanning}
                              />
                            </Card>
                          </div>
                        </div>
                      </div>
                    </OnlyWith>
                    <div className="col-12 pt-3">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="field.doorsAuthorized" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formDoorsAuthorized" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.doorsAuthorized" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              {_map(mCarpark.sortDoors(doors), (door) => (
                                <div key={door.id} className="col-12">
                                  <CarparkDoorItem
                                    lightMode={false}
                                    withShadow={false}
                                    door={door}
                                    toggle={{
                                      toggled: _includes(doorsIds, door.id),
                                      onToggle: () => this.handleToggleDoor(door.id),
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 pt-3">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="field.restrictedByPermanentExit" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formPermanentExit" />
                              ),
                            },
                            {
                              title: (
                                <LocalizedMessage id="field.restrictedByForbiddenEntry" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formForbiddenEntry" />
                              ),
                            },
                            {
                              title: (
                                <LocalizedMessage id="field.restrictedByForbiddenExit" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formForbiddenExit" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.options" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              <div className="col-12">
                                <Checkbox
                                  label={
                                    <LocalizedMessage id="field.restrictedByPermanentExit" />
                                  }
                                  checked={restrictedByPermanentExit}
                                  onChange={(checked) => onFieldsChange({
                                    restrictedByPermanentExit: checked,
                                  })}
                                />
                              </div>
                              <div className="col-12">
                                <Checkbox
                                  label={
                                    <LocalizedMessage id="field.restrictedByForbiddenEntry" />
                                  }
                                  checked={restrictedByForbiddenEntry}
                                  onChange={(checked) => onFieldsChange({
                                    restrictedByForbiddenEntry: checked,
                                  })}
                                />
                              </div>
                              <div className="col-12">
                                <Checkbox
                                  label={
                                    <LocalizedMessage id="field.restrictedByForbiddenExit" />
                                  }
                                  checked={restrictedByForbiddenExit}
                                  onChange={(checked) => onFieldsChange({
                                    restrictedByForbiddenExit: checked,
                                  })}
                                />
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 left-spaced-buttons d-flex">
                    <TextButton
                      label={<LocalizedMessage id="button.prev" />}
                      onClick={this.handlePrev}
                    />
                  </div>
                </StepContent>
              </Step>
            </Stepper>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

ParkingAddForm.propTypes = {
  intl: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  timezone: PropTypes.string.isRequired,
  fixedCustomer: cpmCustomerDataShape,
  addedCustomer: cpmCustomerDataShape,
  uniqueKeypadCode: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    result: PropTypes.string,
  }).isRequired,
  plannings: PropTypes.arrayOf(cpmPlanningDataShape),
  doors: PropTypes.arrayOf(cpmDoorDataShape).isRequired,
  customerAdd: PropTypes.shape({
    added: PropTypes.bool.isRequired,
    result: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  planningsLoading: PropTypes.bool.isRequired,
  planningAdd: PropTypes.shape({
    added: PropTypes.bool.isRequired,
    result: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  forbidManualKeypadCodes: PropTypes.bool.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  onAddCustomer: PropTypes.func,
  onAddPlanning: PropTypes.func,
  handleGetCustomer: PropTypes.func.isRequired,
  handleGetUniqueKeypadCode: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
  handleDisplayConfirm: PropTypes.func.isRequired,
  parkingCategories: cpmParkingCategoriesShape.isRequired,
};

ParkingAddForm.defaultProps = {
  fixedCustomer: null,
  addedCustomer: null,
  plannings: null,
  operationError: null,
  errors: null,
  onAddCustomer: null,
  onAddPlanning: null,
};

const mapFieldsToProps = {
  automaticKeypadCodesQuantity: {
    defaultValue: 1,
  },
  customer: {
    defaultValue: null,
  },
  begin: {
    defaultValue: null,
  },
  end: {
    defaultValue: null,
  },
  spacesQuantity: {
    defaultValue: 1,
  },
  comments: {
    defaultValue: '',
  },
  keypadCodes: {
    defaultValue: [],
  },
  vehicles: {
    defaultValue: [],
  },
  insideQuantity: {
    defaultValue: 0,
  },
  outsideQuantity: {
    defaultValue: 1,
  },
  outsideQuantityPermanentExit: {
    defaultValue: 0,
  },
  planningId: {
    defaultValue: null,
  },
  restrictedByForbiddenEntry: {
    defaultValue: false,
  },
  restrictedByPermanentExit: {
    defaultValue: false,
  },
  restrictedByForbiddenExit: {
    defaultValue: false,
  },
  restrictedByAntipassbackEntrance: {
    defaultValue: false,
  },
  restrictedByAntipassbackExit: {
    defaultValue: false,
  },
  doorsIds: {
    defaultValue: [],
  },
  isAssigned: {
    defaultValue: false,
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  operationError: {
    ...ownProps.operationError,
    messageValuesExtractors: {
      begin: [
        {
          key: 'date',
          pattern: RegexUtils.dateTime,
          setTransform: (value, locOwnProps) => locOwnProps.intl.formatDate(value, standardDateNoSecondsOptions),
        },
        {
          key: 'date',
          pattern: RegexUtils.dateTimeWithFractionalSeconds,
          setTransform: (value, locOwnProps) => locOwnProps.intl.formatDate(value, standardDateNoSecondsOptions),
        },
      ],
      end: [
        {
          key: 'date',
          pattern: RegexUtils.dateTime,
          setTransform: (value, locOwnProps) => locOwnProps.intl.formatDate(value, standardDateNoSecondsOptions),
        },
        {
          key: 'date',
          pattern: RegexUtils.dateTimeWithFractionalSeconds,
          setTransform: (value, locOwnProps) => locOwnProps.intl.formatDate(value, standardDateNoSecondsOptions),
        },
      ],
    },
  },
});

const mapStateToProps = (state) => ({
  operationError: _get(state, 'cpmCarparkParking.add.error', null),
  plannings: _get(state, 'cpmCarparkPlanning.list.elements', []),
  planningsLoading: _get(state, 'cpmCarparkPlanning.list.loading', false),
  planningAdd: _get(state, 'cpmCarparkPlanning.add', false),
  addedCustomer: _get(state, 'cpmCarparkCustomer.current.data', null),
  customerAdd: _get(state, 'cpmCarparkCustomer.add', false),
  doors: _get(state, 'cpmCarpark.current.data.doors', []),
  forbidManualKeypadCodes: _get(state, 'cpmCarpark.current.data.settings.general.forbidManualKeypadCodes', false),
  uniqueKeypadCode: _get(state, 'cpmCarpark.uniqueKeypadCode', ''),
  parkingCategories: _get(state, 'cpmCarparkParkingCategory.list.elements', []),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  handleGetCustomer: (data) => {
    dispatch(
      cpmGetCarparkCustomer(
        mCustomer.queGetCustomer(ownProps.hasPermission),
        data,
      ),
    );
  },
  handleGetUniqueKeypadCode: (data) => {
    dispatch(
      cpmGetCarparkUniqueKeypadCode(mCarpark.queGetUniqueKeypadCode, data),
    );
  },
});

module.exports = compose(
  injectIntl,
  withPermissions,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withFormManagement(
    mapFieldsToProps,
    mapOwnPropsToResources,
    false,
  ),
  withStateReinit(['addCarparkParking']),
  withConfirmDialog,
)(ParkingAddForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/parkings/ParkingAddForm.js